import { useCallback, useEffect, useState } from "react";

/**
 * De werkmodus die je in de vestigingskaart kiest: de asieladministratie (`asiel`), de
 * pensionadministratie (`pension`) of het crematorium (`crematorium`) — de modulefamilies die een
 * vestiging naast elkaar kan draaien. Per browser onthouden (localStorage), zodat je na een
 * herlaadbeurt in dezelfde administratie terugkomt.
 *
 * The mode is shell state only: it says which administration the operator is working in. Screens
 * that need it read it through {@link useWerkmodus}; nothing is scoped to it automatically.
 */
export type Werkmodus = "asiel" | "pension" | "crematorium";

/** Alles behalve de standaard; wat hier niet in staat valt terug op asiel. */
const ANDERE: readonly Werkmodus[] = ["pension", "crematorium"];

const STORAGE_KEY = "fawkes.werkmodus";
/** Same-tab notification: `storage` only fires in *other* tabs, so hooks sync through this event. */
const CHANGE_EVENT = "fawkes:werkmodus";

export const DEFAULT_WERKMODUS: Werkmodus = "asiel";

export function readWerkmodus(): Werkmodus {
  try {
    const opgeslagen = globalThis.localStorage?.getItem(STORAGE_KEY);
    return ANDERE.find((modus) => modus === opgeslagen) ?? DEFAULT_WERKMODUS;
  } catch {
    return DEFAULT_WERKMODUS;
  }
}

export function writeWerkmodus(modus: Werkmodus): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, modus);
  } catch {
    // A blocked/full localStorage just costs us the persistence — never break the switch over it.
  }
  globalThis.dispatchEvent?.(new CustomEvent(CHANGE_EVENT, { detail: modus }));
}

/** `[modus, setModus]` — persisted and shared between every mounted consumer. */
export function useWerkmodus(): [Werkmodus, (modus: Werkmodus) => void] {
  const [modus, setModus] = useState<Werkmodus>(readWerkmodus);

  useEffect(() => {
    const sync = () => setModus(readWerkmodus());
    globalThis.addEventListener?.(CHANGE_EVENT, sync);
    globalThis.addEventListener?.("storage", sync);
    return () => {
      globalThis.removeEventListener?.(CHANGE_EVENT, sync);
      globalThis.removeEventListener?.("storage", sync);
    };
  }, []);

  const set = useCallback((next: Werkmodus) => {
    writeWerkmodus(next);
    setModus(next);
  }, []);

  return [modus, set];
}
